import type { Locale } from "@/i18n/routing";
import type { LocalizedText } from "@/lib/validation/json";

/**
 * Pick the best available string from a bilingual JSON column.
 *
 * The parameter is the column's own type rather than `unknown`, which is what
 * lets every caller pass `product.name` straight through instead of casting it
 * to `Record<string, string>` first. The type comes from
 * `@/lib/validation/json`, deliberately not from the ambient
 * `PrismaJson` namespace — app code should not depend on that.
 *
 * Both runtime guards below outlive the type, for the same reason: `LocalizedText`
 * describes what the column *claims*, and nothing validates a row on read. The
 * annotation makes bad shapes unwritable through Prisma, not absent from the
 * table — raw SQL, an older client, a restored dump or an external integration
 * can all still put one there.
 *
 * - The `??` chain covers a partial `{ ar }`, which must not render `undefined`.
 * - The `typeof value === "string"` branch covers a bare JSON string. The
 *   narrowing in TRY-47 dropped it, which turned such a row from "renders its
 *   text" into "renders nothing" — observed as an empty `<h1>`, `<title>` and
 *   `alt`. The cast is needed because the parameter type says this cannot happen.
 */
export function pickLocale(
	value: LocalizedText | null | undefined,
	locale: Locale,
	fallback = "",
): string {
	if (!value) return fallback;
	if (typeof value === "string") return value as string;
	return value[locale] ?? value.en ?? value.ar ?? fallback;
}

/**
 * The same choice, for bilingual content stored as an `*Ar` / `*En` **column
 * pair** rather than a Json `{ar,en}` column. Both storage shapes exist in the
 * schema (see `fields.ts` § Bilingual pairs); `Tenant.name` and `Currency.name`
 * are the pairs.
 *
 * `||` rather than `??` throughout, deliberately: an empty string is what a
 * half-filled row actually holds — the column is `NOT NULL`, so a market whose
 * English name was never set stores `""` and not `null`, and `??` would render
 * the blank. The whole point of a fallback here is that a name never renders as
 * nothing.
 *
 * `locale` is a plain `string` because most callers hold `tenant.defaultLocale`,
 * which is a `String` column and not the request's `Locale` union.
 */
export function pickPair(
	ar: string | null | undefined,
	en: string | null | undefined,
	locale: string,
	fallback = "",
): string {
	return (locale === "ar" ? ar : en) || en || ar || fallback;
}
